import axios from "axios";
import React, { useState, useEffect, useRef } from "react";
import { IoIosCall } from "react-icons/io";
import { IoLocationOutline } from "react-icons/io5";
import { Link } from "react-router-dom";
import { BASE_URL } from "../../../constant/api";
import { IoLogoWhatsapp } from "react-icons/io";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function SearchNav() {
  const [searchResult, setSearchResult] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(null);
  const searchResultRef = useRef([]);
  const inputRef = useRef(null);
  const containerRef = useRef(null); // Used to detect outside clicks

  useEffect(() => {
    if (searchQuery.length > 0) {
      fetchSearchResults(searchQuery);
    } else {
      setSearchResult([]);
      setSelectedIndex(null);
    }
  }, [searchQuery]);

  const fetchSearchResults = async (query) => {
    try {
      const response = await axios.post(`${BASE_URL}/products/search/`, {
        search_word: query,
      });
      if (
        response.status === 200 &&
        response.data.Status === "1" &&
        response.data.Data.length > 0
      ) {
        setSearchResult(response.data.Data);
        setSelectedIndex(null);
      } else {
        setSearchResult([]);
        setSelectedIndex(null);
      }
    } catch (error) {
      console.log("Error fetching search results:", error);
      setSearchResult([]);
      setSelectedIndex(null);
    }
  };

  const handleKeyDown = (e) => {
    if (searchResult.length === 0) return;

    if (e.key === "ArrowDown") {
      setSelectedIndex((prevIndex) =>
        prevIndex === null || prevIndex === searchResult.length - 1
          ? 0
          : prevIndex + 1
      );
      e.preventDefault();
    } else if (e.key === "ArrowUp") {
      setSelectedIndex((prevIndex) =>
        prevIndex === null || prevIndex === 0
          ? searchResult.length - 1
          : prevIndex - 1
      );
      e.preventDefault();
    } else if (e.key === "Enter") {
      if (selectedIndex !== null && searchResult[selectedIndex]) {
        window.location.href = `/product-details/${searchResult[selectedIndex].id}`;
      } else if (searchQuery.trim() !== "") {
        window.location.href = `/search?query=${encodeURIComponent(searchQuery.trim())}`;
      }
      setSearchResult([]);
      setSelectedIndex(null);
    }
  };

  useEffect(() => {
    if (selectedIndex !== null && searchResultRef.current[selectedIndex]) {
      searchResultRef.current[selectedIndex].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [selectedIndex]);

  // Detect outside clicks
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setSearchResult([]);
        setSelectedIndex(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleWhatsAppClick = () => {
    const phoneNumber = "+919656033444";
    const message = "hi, i need a help";
    const encodedMessage = encodeURIComponent(message);
    const url = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    window.open(url, "_blank");
  };

  const handlePhoneCall = () => {
    const phoneNumber = "+919656033444";
    if (/Mobi|Android/i.test(navigator.userAgent)) {
      window.location.href = `tel:${phoneNumber}`;
    } else {
      navigator.clipboard
        .writeText(phoneNumber)
        .then(() => {
          toast.success(`Phone number copied: ${phoneNumber}`, {
            position: "top-center",
            autoClose: 3000,
          });
        })
        .catch((err) => {
          toast.error("Failed to copy phone number", {
            position: "top-center",
            autoClose: 3000,
          });
          console.error("Failed to copy phone number: ", err);
        });
    }
  };

  return (
    <div ref={containerRef}>
      <ToastContainer position="top-center" autoClose={3000} />
      <div className="flex flex-grow items-center">
        <div className="relative flex-grow w-full" onKeyDown={handleKeyDown}>
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => {
              if (searchQuery.trim().length > 0) {
                fetchSearchResults(searchQuery);
              }
            }}
            placeholder="Search"
            className="w-full border rounded-lg py-2 pl-4 focus:outline-none border-green-700"
            tabIndex="0"
          />

        </div>

        {searchResult.length > 0 && (
          <div className="absolute top-full right-3 w-full md:w-1/2 mt-2 bg-white border border-gray-300 rounded-md shadow-lg max-h-96 overflow-y-auto z-50 md:ms-10">
            <ul className="p-2 space-y-4">
              {searchResult.map((product, index) => (
                <li
                  key={product.id}
                  className={`flex items-center transition border-b p-2 ease-in duration-200 cursor-pointer ${selectedIndex === index ? "bg-gray-100" : "hover:bg-gray-100"
                    }`}
                  ref={(el) => (searchResultRef.current[index] = el)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <Link
                    to={`/product-details/${product.id}`}
                    className="flex w-full"
                    onClick={() => {
                      setSelectedIndex(index);
                      setSearchResult([]); 
                      // setSearchQuery("");   // 👈 Optional: clear input box
                    }}
                  >
                    <img
                      src={
                        product.product_images?.[0]?.image
                          ? product.product_images[0].image.startsWith(BASE_URL)
                            ? product.product_images[0].image
                            : `${BASE_URL}${product.product_images[0].image}`
                          : "/images/default-product.png"
                      }
                      alt={product.name}
                      className="w-16 h-16 object-cover rounded mr-4"
                    />
                    <div>
                      <h3 className="font-semibold">{product.name}</h3>
                      <p className="text-gray-500">
                        <span className="line-through text-sm mr-2">{product.currency_symbol}{product.actual_price}</span>
                        <span className="font-bold">{product.currency_symbol}{product.price}</span>
                        <span className="text-green-600 ml-2">({product.offer_percent}% OFF)</span>
                      </p>
                      <p className="text-sm text-gray-600 line-clamp-3">
                        {product.description}
                      </p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export default SearchNav;
